/**
 * Pure status derivation for every card on the Connected Apps page.
 *
 * Each integration exposes its own bespoke shape (QuickBooks has a sync
 * status, Stripe has capability flags, Wisetack has an enrollment state
 * machine, webhooks have failure counters). These functions collapse all of
 * that into the one vocabulary the page reasons about — a health, a human
 * label, and an optional reason to hoist into the attention banner — so the
 * summary counts, filters and banner can be written once.
 *
 * Everything here is deliberately free of React and of the query layer: the
 * mapping from "raw API payload" to "what the admin sees" is the part worth
 * pinning down in tests.
 */
import type {
  PayabliConnection,
  PaymentSettings,
  QuickBooksStatus,
  WisetackSettings,
} from '@/types/settings';
import type { PartnerApiKey } from '@/types/partnerApiKey';
import type { WebhookSubscriptionView } from '@/types/webhook-subscription';
import type { IntegrationHealth } from './integration-tokens';

export interface IntegrationStatus {
  health: IntegrationHealth;
  label: string;
  /** Present only when the card belongs in the "needs attention" banner. */
  attentionReason?: string;
}

const UNREADABLE: IntegrationStatus = {
  health: 'error',
  label: 'Status unavailable',
  attentionReason: 'We could not read the current status.',
};

export function quickBooksStatus(
  data: QuickBooksStatus | undefined,
  isError: boolean,
): IntegrationStatus {
  if (isError && !data) return UNREADABLE;
  if (!data?.connected) return { health: 'disconnected', label: 'Not connected' };
  if (data.syncStatus === 'error') {
    return {
      health: 'attention',
      label: 'Sync error',
      attentionReason: 'The last accounting sync failed.',
    };
  }
  if (data.syncStatus === 'syncing') return { health: 'pending', label: 'Syncing…' };
  // `syncEnabled: false` just means every entity direction is still "off" —
  // a deliberate config choice, not a fault. It stays a detail field rather
  // than an amber flag so the banner only ever carries real problems.
  return { health: 'connected', label: 'Connected' };
}

export function stripeStatus(
  data: PaymentSettings | undefined,
  isError: boolean,
): IntegrationStatus {
  if (isError && !data) return UNREADABLE;
  const stripe = data?.stripe;
  if (!stripe?.accountId) return { health: 'disconnected', label: 'Not connected' };

  const requirementCount =
    (stripe.requirements?.pastDue.length ?? 0) + (stripe.requirements?.currentlyDue.length ?? 0);
  if (requirementCount > 0) {
    return {
      health: 'attention',
      label: 'Action required',
      attentionReason:
        requirementCount === 1
          ? '1 requirement is blocking payouts.'
          : `${requirementCount} requirements are blocking payouts.`,
    };
  }
  if (stripe.status === 'restricted') {
    return {
      health: 'attention',
      label: 'Restricted',
      attentionReason: 'Stripe has restricted this account.',
    };
  }
  if (stripe.status === 'pending') return { health: 'pending', label: 'Setup incomplete' };
  if (stripe.chargesEnabled === false || stripe.payoutsEnabled === false) {
    return {
      health: 'attention',
      label: stripe.chargesEnabled === false ? 'Charges disabled' : 'Payouts disabled',
      attentionReason: 'Finish Stripe onboarding to enable charges and payouts.',
    };
  }
  return { health: 'connected', label: 'Connected' };
}

export function payabliStatus(
  data: PayabliConnection | undefined,
  isError: boolean,
): IntegrationStatus {
  if (isError && !data) return UNREADABLE;
  if (!data?.connected) return { health: 'disconnected', label: 'Not configured' };
  // Valid credentials are only half of a working connection. Payabli must also
  // be told where to deliver notifications for this paypoint; until it is, the
  // tenant can take payments while every settlement, refund, ACH return and
  // chargeback goes unrecorded — behind an otherwise green badge.
  if (data.webhooksRegistered === false) {
    return {
      health: 'attention',
      label: 'Webhooks not registered',
      attentionReason:
        data.webhookRegistrationError ??
        'Payabli is not sending payment notifications to Notifi, so payment updates will not be recorded.',
    };
  }
  return { health: 'connected', label: 'Configured' };
}

const WISETACK_STATE_LABELS: Record<WisetackSettings['enrollmentState'], string> = {
  not_configured: 'Not enrolled',
  not_enrolled: 'Not enrolled',
  in_progress: 'Application in progress',
  submitted: 'Under review',
  approved: 'Approved',
  declined: 'Declined',
  // Wisetack treats an expired application as never-enrolled: the merchant's
  // only move is a fresh signup, which is exactly the not-enrolled surface.
  expired: 'Not enrolled',
};

export function wisetackStatus(
  data: WisetackSettings | undefined,
  isError: boolean,
): IntegrationStatus {
  if (isError && !data) return UNREADABLE;
  if (!data) return { health: 'disconnected', label: 'Not configured' };
  if (!data.enabled) return { health: 'unavailable', label: 'Not available' };

  const label = WISETACK_STATE_LABELS[data.enrollmentState] ?? 'Not enrolled';
  switch (data.enrollmentState) {
    case 'approved':
      return { health: 'connected', label };
    case 'in_progress':
    case 'submitted':
      return { health: 'pending', label };
    case 'declined':
      return {
        health: 'attention',
        label,
        attentionReason: 'Wisetack declined the financing application.',
      };
    // `expired` deliberately falls through to `disconnected` with the rest of
    // the never-enrolled states: Wisetack asked for it to read exactly like
    // "Not enrolled", and an attention banner over a signup CTA is noise.
    default:
      return { health: 'disconnected', label };
  }
}

export function reviewPlatformsStatus(
  connectedCount: number,
  isError: boolean,
  hasData: boolean,
): IntegrationStatus {
  if (isError && !hasData) return UNREADABLE;
  if (connectedCount === 0) return { health: 'disconnected', label: 'No sources connected' };
  return {
    health: 'connected',
    label: connectedCount === 1 ? '1 source connected' : `${connectedCount} sources connected`,
  };
}

export interface CompanyCamStatusInput {
  /** False when the deployment itself has no CompanyCam credentials. */
  available: boolean;
  connected: boolean;
  status: string | null;
  webhookRegistered?: boolean;
  /** Photos whose export failed and ran out of retries. */
  failedExportCount?: number;
}

/**
 * CompanyCam health.
 *
 * Two distinctions carry the weight. `unavailable` is not `disconnected`: the
 * former is an operator problem the tenant cannot fix, so the card must not
 * offer a connect button that 503s. And a connection with no registered
 * webhook is `attention`, not `connected` — it looks healthy while silently
 * importing nothing, which is the failure mode that is hardest to notice.
 */
export function companyCamStatus(
  input: CompanyCamStatusInput | undefined,
  isError: boolean,
): IntegrationStatus {
  if (isError && !input) return UNREADABLE;
  // No payload and no error means the query has not resolved yet. Reporting
  // UNREADABLE here put a red "we could not read the current status" banner on
  // the page for every tenant on every load, until the fetch came back —
  // indistinguishable from a genuinely broken integration. Fall through to the
  // neutral state, as every other card on this page does.
  if (!input) return { health: 'disconnected', label: 'Not connected' };

  if (!input.available) {
    return { health: 'unavailable', label: 'Not configured' };
  }
  if (input.status === 'NEEDS_REAUTH') {
    return {
      health: 'attention',
      label: 'Reconnect required',
      attentionReason: 'CompanyCam access expired. Reconnect to resume syncing.',
    };
  }
  if (!input.connected) {
    return { health: 'disconnected', label: 'Not connected' };
  }
  if (input.webhookRegistered === false) {
    return {
      health: 'attention',
      label: 'Photos not syncing automatically',
      attentionReason:
        'CompanyCam is connected but no webhook is registered, so new photos ' +
        'will not arrive on their own. Reconnect to retry.',
    };
  }
  // Last of the attention states on purpose. An expired grant and a missing
  // webhook are causes a tenant can act on; stranded photos are a symptom, and
  // when both are true naming the cause is more useful.
  const stranded = input.failedExportCount ?? 0;
  if (stranded > 0) {
    return {
      health: 'attention',
      label: stranded === 1 ? '1 photo not sent' : `${stranded} photos not sent`,
      attentionReason:
        `${stranded === 1 ? 'One photo' : `${stranded} photos`} could not be ` +
        'sent to CompanyCam after repeated attempts, and will not be retried ' +
        'again. They are still on the job in Notifi.',
    };
  }
  return { health: 'connected', label: 'Connected' };
}

export function webhooksStatus(
  subscriptions: WebhookSubscriptionView[] | undefined,
  isError: boolean,
): IntegrationStatus {
  if (isError && !subscriptions) return UNREADABLE;
  if (!subscriptions || subscriptions.length === 0) {
    return { health: 'disconnected', label: 'No endpoints' };
  }

  const enabled = subscriptions.filter((subscription) => subscription.enabled);
  const failing = enabled.filter((subscription) => subscription.failureCount > 0);
  if (failing.length > 0) {
    return {
      health: 'attention',
      label: failing.length === 1 ? '1 endpoint failing' : `${failing.length} endpoints failing`,
      attentionReason: 'Recent deliveries failed on at least one endpoint.',
    };
  }
  if (enabled.length === 0) return { health: 'disconnected', label: 'All endpoints paused' };
  return {
    health: 'connected',
    label: enabled.length === 1 ? '1 endpoint enabled' : `${enabled.length} endpoints enabled`,
  };
}

export function chatbotStatus(enabled: boolean, isError: boolean): IntegrationStatus {
  if (isError) return UNREADABLE;
  return enabled
    ? { health: 'connected', label: 'Enabled' }
    : { health: 'disconnected', label: 'Disabled' };
}

export function zapierStatus(connected: boolean, isError: boolean): IntegrationStatus {
  if (isError) return UNREADABLE;
  return connected
    ? { health: 'connected', label: 'Connected' }
    : { health: 'disconnected', label: 'Not connected' };
}

/**
 * The Partner API card covers two things at once — issued credentials and the
 * signed change-notification endpoints they drive — so it counts as connected
 * when *either* exists. A failing endpoint outranks both: a partner silently
 * missing notifications is the failure worth an amber flag.
 */
export function partnerApiStatus(
  apiKeys: PartnerApiKey[] | undefined,
  subscriptions: WebhookSubscriptionView[] | undefined,
  isError: boolean,
): IntegrationStatus {
  if (isError && !apiKeys && !subscriptions) return UNREADABLE;

  const activeKeys = apiKeys?.filter((key) => key.status === 'active').length ?? 0;
  const enabled = subscriptions?.filter((subscription) => subscription.enabled) ?? [];
  const failing = enabled.filter((subscription) => subscription.failureCount > 0);

  if (failing.length > 0) {
    return {
      health: 'attention',
      label: failing.length === 1 ? '1 endpoint failing' : `${failing.length} endpoints failing`,
      attentionReason: 'Recent partner notifications failed to deliver.',
    };
  }
  if (activeKeys === 0 && enabled.length === 0) {
    return { health: 'disconnected', label: 'Not configured' };
  }
  return { health: 'connected', label: 'Configured' };
}
